import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CalendarDays, ExternalLink, Loader2, Mail, Phone, User, Package, ListChecks } from 'lucide-react';

/**
 * Ficha rápida del lead desde el Pipeline (y cualquier tablero).
 * No depende de la pantalla de Leads ni de deep-links frágiles.
 */
export default function LeadQuickSheet({ leadId, open, onClose }) {
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !leadId) {
      setLead(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/tenant/crm/leads/${leadId}`)
      .then((r) => {
        if (!cancelled) setLead(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(formatApiError(e.response?.data?.detail) || 'No se pudo cargar el lead');
          toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo abrir la ficha');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, leadId]);

  return (
    <Dialog open={!!open && !!leadId} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto z-[100]"
        data-testid="pipeline-lead-sheet"
      >
        <DialogHeader>
          <DialogTitle className="pr-6">
            {loading ? 'Cargando ficha…' : (lead?.display_name || 'Lead')}
            {lead?.display_code && (
              <span className="ml-2 text-xs font-mono font-normal text-[#94A3B8]">{lead.display_code}</span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Detalle del lead desde el pipeline
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-[#64748B]">
            <Loader2 className="w-5 h-5 animate-spin" /> Cargando datos del lead…
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
            <div className="mt-2">
              <Button size="sm" variant="outline" onClick={onClose}>Cerrar</Button>
            </div>
          </div>
        )}

        {!loading && lead && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-[#94A3B8]" />
                <span className="text-[#64748B]">Email:</span>
                <span className="font-medium text-[#0F172A] truncate">{lead.email || '—'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-[#94A3B8]" />
                <span className="text-[#64748B]">Tel:</span>
                <span className="font-medium text-[#0F172A]">{lead.phone || '—'}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:col-span-2">
                <User className="w-3.5 h-3.5 text-[#94A3B8]" />
                <span className="text-[#64748B]">Responsable:</span>
                <span className={lead.assigned_to_name ? 'font-medium text-emerald-700' : 'text-amber-600'}>
                  {lead.assigned_to_name || 'Sin asignar'}
                </span>
              </div>
              {lead.assignment_pool_name && (
                <div className="sm:col-span-2 text-xs">
                  <span className="text-[#64748B]">Equipo:</span>{' '}
                  <span className="font-medium text-blue-600">{lead.assignment_pool_name}</span>
                </div>
              )}
              <div className="sm:col-span-2 text-xs">
                <span className="text-[#64748B]">Fuente:</span> {lead.source_type || '—'}
                {lead.source_name ? ` · ${lead.source_name}` : ''}
              </div>
              {lead.interest_summary && (
                <div className="sm:col-span-2 text-xs">
                  <span className="text-[#64748B]">Interés:</span> {lead.interest_summary}
                </div>
              )}
              {lead.deal_name && (
                <div className="sm:col-span-2 text-xs">
                  <span className="text-[#64748B]">Oportunidad:</span> {lead.deal_name}
                </div>
              )}
            </div>

            {lead.ai_last_summary && (
              <div className="p-2.5 bg-violet-50 border border-violet-200 rounded-lg text-xs text-violet-900">
                {lead.ai_last_summary}
              </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1 border-t border-[#F1F5F9]">
              <Button
                size="sm"
                onClick={() => {
                  onClose?.();
                  navigate(`/tenant/crm/leads?lead_id=${lead.id}`);
                }}
                data-testid="pipeline-lead-open-full"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Ficha completa
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onClose?.();
                  navigate(`/tenant/agenda/new?lead_id=${lead.id}`);
                }}
              >
                <CalendarDays className="w-3.5 h-3.5 mr-1" /> Agendar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onClose?.();
                  navigate(`/tenant/pos?crm_lead_id=${lead.id}${lead.deal_id ? `&crm_deal_id=${lead.deal_id}` : ''}`);
                }}
              >
                <Package className="w-3.5 h-3.5 mr-1" /> Venta
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onClose?.();
                  navigate(`/tenant/crm/leads?lead_id=${lead.id}`);
                }}
              >
                <ListChecks className="w-3.5 h-3.5 mr-1" /> Tareas
              </Button>
              <Button size="sm" variant="ghost" onClick={onClose}>Cerrar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
